import React, { useRef, useEffect } from "react";
import "./clientQuotesDisplay.css";
import BdrDisclosure from "../../../../components/BdrDisclosure/BdrDisclosure";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { useMediaQuery } from "@mui/material";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import BAGroupTimeFormat from "../../../../utils/formatters/BAGroupTimeFormat";
import { copyAsyncString } from "../../../../utils/formatters/copyStringToClipboard";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useMutation } from "@apollo/client";
import { GET_PUBLIC_SHARE_URL } from "../../../../utils/GraphQL/mutations";

/*
 * Two hosts, one component. `inPanel` renders this straight into the client
 * console's VS panel: no BdrDisclosure wrapper (the panel header already names
 * the section), always open, and none of the document-scroll behaviour — the
 * console's page is viewport-locked and the PANEL scrolls.
 *
 * Without it, this is the collapsible row the Edit branch still uses,
 * unchanged. `barCollapsed` is a PARENT-owned prop, which is why panel mode
 * ignores it rather than forcing it open from in here.
 */
const ClientQuotesDisplay = ({
  heightTrigger,
  quotesArr,
  openQuoteModal,
  barCollapsed,
  setBarCollapsed,
  clickTrigger,
  openEditInvoiceQuoteModal,
  inPanel,
}) => {
  const { handleSnack } = useSnackbar();
  const smaller900 = useMediaQuery("(max-width:900px)");
  const clientQuotesDiv = useRef();
  const [getPublicShareUrl] = useMutation(GET_PUBLIC_SHARE_URL);

  // An active quote is a live opportunity — the one thing worth surfacing on
  // the collapsed bar. Invoiced and expired ones are history.
  const activeQuotes =
    quotesArr?.filter((quote) => /active/gi.test(quote?.quoteStatus))?.length ||
    0;

  useEffect(() => {
    if (clickTrigger > 0) {
      openBarCollapse();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickTrigger]);

  const openBarCollapse = () => {
    if (heightTrigger) {
      heightTrigger();
    }

    // The console's page does not scroll; its panel does.
    if (inPanel) return;

    setTimeout(() => {
      if (smaller900 === true && barCollapsed === true) {
        const navHeight = document.querySelector(
          "#admin-nav-height-marker"
        )?.offsetHeight;
        const pagePos = clientQuotesDiv.current.offsetTop;

        const totalMobileScroll = pagePos - navHeight - 5;
        window.scrollTo(0, totalMobileScroll);
      }

      // if (smaller900 === false && barCollapsed === true) {
      //   clientQuotesDiv.current.scrollIntoView({ block: "start" });
      // }
    }, 500);

    setBarCollapsed((prev) => !prev);
  };

  const listBody = (
    quotesArr?.length > 0 ? (
          <div className="client-quotes-info-display">
            <ul>
              {quotesArr?.map((quote, index) => {
                let bulletColor = /active/gi.test(quote?.quoteStatus)
                  ? "yellow"
                  : /invoiced/gi.test(quote?.quoteStatus)
                  ? "green"
                  : "red"; // default to expired;

                const viewCount = quote?.quoteViewed?.filter(
                  (view) => view?.isUser === false
                )?.length;
                const total = priceStrFormatter(quote?.quoteTotal || "0");
                /*
                 * A quote the client has opened and not acted on is the one
                 * worth noticing - it is the shape the Activity feed surfaced
                 * on real data. Only call it out while the quote is still
                 * live; views on an invoiced or expired quote are history.
                 */
                const isActive = /active/gi.test(quote?.quoteStatus);
                const viewed = viewCount > 0;

                return (
                  <li key={`quote-item-${quote?.id}-${index}`}>
                    <div
                      className="client-quote-item-box bdr-flex-row-between bdr-border-thin"
                      onClick={() => {
                        openQuoteModal(quote?.id);
                      }}
                    >
                      <div className="client-quote-item-info-box">
                        <span
                          className={`client-quote-circle bullet-color-${bulletColor}`}
                        />
                        <span className="quote-item-label">
                          <span className="quote-item-num">
                            {quote?.quoteNum}
                          </span>
                          <span className="quote-item-date">
                            {BAGroupTimeFormat(
                              quote?.dateCreated,
                              false,
                              true,
                              true
                            )}
                          </span>
                          <span
                            className={`quote-item-views${
                              viewed && isActive ? " is-engaged" : ""
                            }`}
                            title={
                              viewed
                                ? `Client opened this quote ${viewCount} time${
                                    viewCount === 1 ? "" : "s"
                                  }`
                                : "The client has not opened this quote"
                            }
                          >
                            {viewCount}
                            <VisibilityIcon
                              sx={{ fontSize: "16px", marginLeft: "3px" }}
                              titleAccess="Quote views"
                            />
                          </span>
                        </span>
                        {/* The total was the one thing this row never said. It
                            goes in the middle, which was dead space. */}
                        <span className="quote-item-total">{total}</span>
                      </div>
                      <div className="client-quote-item-actions-box">
                        {navigator.clipboard && (
                          <button
                            className="client-invoices-quotes-copy-btn"
                            onClick={async (event) => {
                              event.stopPropagation();
                              event.preventDefault();
                              const fallback = `https://bigginsdoorrefinishing.com/client/view-quote/${quote?.id}`;
                              const urlPromise = getPublicShareUrl({
                                variables: { docType: "quote", docId: quote?.id },
                              })
                                .then(({ data }) => data?.getPublicShareUrl || fallback)
                                .catch(() => fallback);
                              const ok = await copyAsyncString(urlPromise, fallback);
                              handleSnack(
                                ok
                                  ? "Quote Link Copied"
                                  : "Failed to copy quote link — try again",
                                ok ? 2500 : 4000
                              );
                            }}
                          >
                            <InsertLinkIcon
                              sx={{ fontSize: "18px" }}
                              titleAccess="Copy Quote Link"
                            />
                          </button>
                        )}
                        <button
                          className="client-invoices-quotes-copy-btn"
                          onClick={async (event) => {
                            event.stopPropagation();
                            event.preventDefault();
                            openEditInvoiceQuoteModal(false, quote?.id);
                          }}
                        >
                          <EditIcon
                            sx={{ fontSize: "16px" }}
                            titleAccess="Edit Quote"
                          />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
    ) : (
      <div className="scc-list-empty">No quotes to display</div>
    )
  );

  if (inPanel) {
    return (
      <div className="scc-list" ref={clientQuotesDiv}>
        {listBody}
      </div>
    );
  }

  return (
    <div
      id="client-quotes-height-marker"
      className="client-quotes-container"
      ref={clientQuotesDiv}
    >
      <BdrDisclosure
        open={!barCollapsed}
        onToggle={openBarCollapse}
        label="Quotes"
        icon={<RequestQuoteIcon />}
        count={quotesArr?.length}
        status={
          activeQuotes > 0
            ? { tone: "accent", text: `${activeQuotes} active` }
            : undefined
        }
        emptyText="No quotes yet"
        bodyScroll="cap"
      >
        {listBody}
      </BdrDisclosure>
    </div>
  );
};

export default ClientQuotesDisplay;
